// Routes configuration for WhatsApp CMS with Multi-Tenant RBAC
import React from 'react';
import {
  BiUserCircle,
  BiCog,
  BiUserCheck,
  BiHistory,
  BiSend,
  BiGroup,
  BiMessageDetail,
  BiCheckShield,
  BiServer,
  BiCreditCard,
  BiWallet,
  BiLineChart,
  BiReceipt,
  BiListUl,
  BiFullscreen,
  BiCalendar,
} from 'react-icons/bi';
import {
  Assessment as AssessmentIcon,
  BarChart as BarChartIcon,
  ManageAccounts as ManageAccountsIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Contacts as ContactsIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import {FaWhatsapp} from 'react-icons/fa';

const routesConfig = [
  {
    id: 'home',
    title: 'Home',
    messageId: 'sidebar.dashboard',
    type: 'item',
    url: '/home',
    icon: <BarChartIcon />,
  },

  // =====================================================
  // PROVIDER PORTAL
  // =====================================================
  {
    id: 'provider',
    title: 'Provider Management',
    messageId: 'sidebar.provider',
    type: 'collapse',
    icon: <ManageAccountsIcon />,
    permittedRole: ['provider'],
    excludeRole: ['superadmin'],
    children: [
      {
        id: 'providerOverview',
        title: 'Business Overview',
        messageId: 'sidebar.providerOverview',
        type: 'item',
        url: '/provider/overview',
        icon: <DashboardIcon />,
      },
      {
        id: 'providerServiceUsers',
        title: 'Manage Service Accounts',
        messageId: 'sidebar.providerServiceUsers',
        type: 'item',
        url: '/provider/service-users',
        icon: <PeopleIcon />,
      },

      {
        id: 'providerPortalUsers',
        title: 'Portal Users',
        messageId: 'sidebar.portalUsers',
        type: 'item',
        url: '/provider/portal-users',
        icon: <BiUserCheck />,
      },
    ],
  },

  // =====================================================
  // USER PORTAL
  // =====================================================
  {
    id: 'userAdmin',
    title: 'User Management',
    messageId: 'sidebar.userAdmin',
    type: 'collapse',
    icon: <BiUserCheck />,
    permittedRole: ['user'],
    children: [
      {
        id: 'userOverview',
        title: 'Account Overview',
        messageId: 'sidebar.userOverview',
        type: 'item',
        url: '/user/overview',
        icon: <DashboardIcon />,
      },

      {
        id: 'userPortalUsers',
        title: 'Portal Users',
        messageId: 'sidebar.portalUsers',
        type: 'item',
        url: '/portal-users',
        icon: <BiUserCheck />,
      },
    ],
  },

  // =====================================================
  // PLATFORM (SUPERADMIN + ADMIN)
  // =====================================================
  {
    id: 'platform',
    title: 'Platform',
    messageId: 'sidebar.platform',
    type: 'collapse',
    icon: <BiCog />,
    auth: ['superadmin'],
    children: [
      {
        id: 'platformOverview',
        title: 'Overview',
        messageId: 'sidebar.platformOverview',
        type: 'item',
        url: '/platform/overview',
        icon: <DashboardIcon />,
        auth: ['superadmin'],
      },
      {
        id: 'platformUsers',
        title: 'All Users',
        messageId: 'sidebar.platformUsers',
        type: 'item',
        url: '/platform/users',
        icon: <PeopleIcon />,
        auth: ['superadmin'],
      },
      {
        id: 'platformSendOnBehalf',
        title: 'Send on Behalf',
        messageId: 'sidebar.platformSendOnBehalf',
        type: 'item',
        url: '/platform/send-on-behalf',
        icon: <BiSend />,
        auth: ['superadmin', 'admin'],
      },
      {
        id: 'platformSendThrottle',
        title: 'Send Throttle',
        messageId: 'sidebar.platformSendThrottle',
        type: 'item',
        url: '/platform/send-throttle',
        icon: <BiCheckShield />,
        auth: ['superadmin', 'admin'],
      },
      {
        id: 'platformThrottleMonitor',
        title: 'Throttle Monitor',
        messageId: 'sidebar.platformThrottleMonitor',
        type: 'item',
        url: '/platform/throttle-monitor',
        icon: <BiServer />,
        auth: ['superadmin', 'admin'],
      },
      {
        id: 'platformPlans',
        title: 'Plans & Pricing',
        messageId: 'sidebar.platformPlans',
        type: 'item',
        url: '/platform/plans',
        icon: <BiCreditCard />,
        auth: ['superadmin'],
      },
      {
        id: 'platformBalanceMonitor',
        title: 'Balance Monitor',
        messageId: 'sidebar.platformBalanceMonitor',
        type: 'item',
        url: '/platform/balance-monitor',
        icon: <BiWallet />,
        auth: ['superadmin'],
      },
      {
        id: 'platformFinancial',
        title: 'Financial',
        messageId: 'sidebar.platformFinancial',
        type: 'item',
        url: '/platform/financial',
        icon: <BiLineChart />,
        auth: ['superadmin'],
      },
      {
        id: 'platformExpenses',
        title: 'Expenses',
        messageId: 'sidebar.platformExpenses',
        type: 'item',
        url: '/platform/expenses',
        icon: <BiReceipt />,
        auth: ['superadmin'],
      },
      {
        id: 'platformPendingApprovals',
        title: 'Pending Approvals',
        messageId: 'sidebar.platformPendingApprovals',
        type: 'item',
        url: '/platform/pending-approvals',
        icon: <BiCheckShield />,
        auth: ['superadmin', 'provider'],
      },
      {
        id: 'platformMonitor',
        title: 'Platform Monitor',
        messageId: 'sidebar.platformMonitor',
        type: 'item',
        url: '/platform/monitor',
        icon: <BiFullscreen />,
        auth: ['superadmin', 'admin'],
      },
      {
        id: 'platformSystemLogs',
        title: 'System Logs',
        messageId: 'sidebar.platformSystemLogs',
        type: 'item',
        url: '/platform/system-logs',
        icon: <BiListUl />,
        auth: ['superadmin'],
      },
    ],
  },

  // =====================================================
  // WHATSAPP SERVICE (ALL ROLES)
  // =====================================================
  {
    id: 'myService',
    title: 'WhatsApp Service',
    messageId: 'sidebar.myService',
    type: 'collapse',
    icon: <FaWhatsapp />,
    children: [
      {
        id: 'serviceConnection',
        title: 'Connection',
        messageId: 'sidebar.serviceConnection',
        type: 'item',
        url: '/service/connection',
        icon: <BiCog />,
      },
      {
        id: 'serviceSend',
        title: 'Send Message',
        messageId: 'sidebar.serviceSend',
        type: 'item',
        url: '/service/send',
        icon: <BiSend />,
      },
      {
        id: 'serviceSendMultiple',
        title: 'Send Multiple',
        messageId: 'sidebar.serviceSendMultiple',
        type: 'item',
        url: '/service/send-multiple',
        icon: <BiMessageDetail />,
      },
      {
        id: 'serviceBulk',
        title: 'Bulk Messages',
        messageId: 'sidebar.serviceBulk',
        type: 'item',
        url: '/service/bulk',
        icon: <BiGroup />,
      },
      {
        id: 'serviceHistory',
        title: 'Message History',
        messageId: 'sidebar.serviceHistory',
        type: 'item',
        url: '/service/history',
        icon: <BiHistory />,
      },
      {
        id: 'serviceReports',
        title: 'Reports',
        messageId: 'sidebar.serviceReports',
        type: 'item',
        url: '/service/reports',
        icon: <AssessmentIcon />,
      },
      {
        id: 'serviceScheduledCampaigns',
        title: 'Scheduled Campaigns',
        messageId: 'sidebar.serviceScheduledCampaigns',
        type: 'item',
        url: '/service/scheduled-campaigns',
        icon: <BiCalendar />,
      },
      {
        id: 'serviceContacts',
        title: 'Contacts',
        messageId: 'sidebar.serviceContacts',
        type: 'item',
        url: '/service/contacts',
        icon: <ContactsIcon />,
      },
      {
        id: 'serviceContactGroups',
        title: 'Contact Groups',
        messageId: 'sidebar.serviceContactGroups',
        type: 'item',
        url: '/service/contact-groups',
        icon: <GroupIcon />,
      },
      {
        id: 'serviceGroups',
        title: 'WhatsApp Groups',
        messageId: 'sidebar.serviceGroups',
        type: 'item',
        url: '/service/groups',
        icon: <FaWhatsapp />,
      },
      {
        id: 'serviceGroupMessages',
        title: 'Group Messages',
        messageId: 'sidebar.serviceGroupMessages',
        type: 'item',
        url: '/service/group-messages',
        icon: <BiGroup />,
      },
    ],
  },

  // =====================================================
  // MY PROFILE
  // =====================================================
  {
    id: 'account',
    title: 'My Profile',
    messageId: 'sidebar.account',
    type: 'collapse',
    icon: <BiUserCircle />,
    children: [
      {
        id: 'profile',
        title: 'Profile',
        messageId: 'sidebar.profile',
        type: 'item',
        url: '/account/profile',
        icon: <BiUserCircle />,
      },
      {
        id: 'settings',
        title: 'Settings',
        messageId: 'sidebar.settings',
        type: 'item',
        url: '/account/settings',
        icon: <BiCog />,
      },
    ],
  },
];

export default routesConfig;
